import { LogoDeMarque } from './LogoDeMarque';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE CARROUSEL DES MARQUES CLIENTES — F-70, ET IL NE VIT QUE SUR L'ACCUEIL.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-70 : « **le carrousel vit sur l'accueil, et sur cette page seule.** Aucun bandeau,
 * aucun pied de page ne le répète. » Et F-69 le redit du pied de page : « il ne porte pas
 * le carrousel ». Ce composant n'a donc **qu'un seul appelant**, et c'en est un de trop
 * pour qu'il vive dans la page : la règle « sur cette page seule » ne se vérifie que si le
 * carrousel est une chose nommée dont on peut compter les usages.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE N'EST PAS UN CARROUSEL QUI DÉFILE, ET LE NOM VIENT DU CAHIER DES CHARGES.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Aucune règle de F-70 ne demande un défilement, une animation ou une pagination — et
 * trois règles l'excluent en pratique : « **aucun plafond de nombre de marques** », « toutes
 * paraissent, sans plafond, dans leur ordre de création », et l'ordre « ne se stocke pas
 * comme un rang ». Une bande qui défile obligerait à décider d'un pas, d'une vitesse et
 * d'un point de départ, **trois valeurs que personne n'a tranchées**, et à masquer une
 * partie des clientes à tout instant — sur une surface dont l'objet est de les nommer.
 *
 * **Ce qui est rendu est donc une grille qui revient à la ligne.** Elle montre toutes les
 * marques, elle s'adapte à tout appareil comme F-73 l'exige des sept pages qui présentent,
 * et elle n'a **aucun état** — ni rang courant, ni minuteur, ni geste.
 *
 * **ET C'EST UNE GRILLE ET NON UNE LISTE VERTICALE, PARCE QUE « AUCUN PLAFOND » EST UNE
 * RÈGLE ET NON UNE HYPOTHÈSE.** La première forme empilait les marques l'une sous l'autre :
 * lisible à deux clientes, et **une page d'accueil de plusieurs écrans de haut à trente** —
 * or §13 le pose comme cas de bord attendu, « toutes les structures du parc ont leurs trois
 * colonnes remplies → **toutes paraissent, sans plafond** ». Un rendu qui ne tient qu'aux
 * premières clientes est un rendu qui casse le jour où le produit marche.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **L'ADRESSE DU SITE D'UNE STRUCTURE REND SON LOGO CLIQUABLE, ET RIEN D'AUTRE NE
 * L'EST.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-70 : « l'**adresse de son propre site**, qui rend son logo cliquable » — colonne
 * facultative, donc **une marque sur deux peut ne pas être un lien**, et c'est un état
 * normal et non un défaut d'affichage.
 *
 * **Le lien sort du produit, donc il porte `rel="noreferrer"`** : sans lui, le site de la
 * cliente apprend de quelle page on vient, ce qui est une information que cette surface a
 * par ailleurs refusé de collecter pour elle-même — « AUCUNE MESURE D'AUDIENCE ». La
 * refuser pour soi et l'offrir à un tiers serait incohérent.
 */

export type MarqueAAfficher = {
  readonly nomAffichage: string;
  readonly logoUrl: string;
  readonly phrase: string | null;
  readonly siteUrl: string | null;
  readonly chiffres: readonly {
    readonly valeur: string;
    readonly libelle: string;
  }[];
};

export function Carrousel({
  marques,
}: {
  readonly marques: readonly MarqueAAfficher[];
}) {
  return (
    <ul className="grid gap-8 sm:grid-cols-2 md:grid-cols-3">
      {marques.map((marque, rang) => (
        // ═══════════════════════════════════════════════════════════════════
        // **LA CLÉ PORTE LE RANG, ET C'EST FAUTE DE MIEUX — PAS PAR PARESSE.**
        // ═══════════════════════════════════════════════════════════════════
        //
        // `CDC-6` §7 range « l'identifiant interne de la structure » parmi les champs
        // **interdits en réponse** : le front n'a donc **aucune clé technique**, par décision
        // et non par oubli.
        //
        // **Le nom d'affichage seul ne suffit pas**, et c'est le défaut qu'une revue a
        // trouvé : deux clientes homonymes — ce que rien n'empêche, l'accord se donnant hors
        // du produit — produiraient **une collision de clés**, donc un rendu où React réutilise
        // l'état d'une tuile pour une autre. L'état ici est celui du chargement d'un logo :
        // la collision ferait afficher le nom d'une marque sous le logo d'une autre.
        //
        // **Le rang est stable pour un chargement donné**, l'ordre de la réponse étant celui
        // de création des structures et « ne se stockant pas comme un rang ». Il n'y a ni
        // tri, ni filtre, ni insertion côté client — les trois cas où une clé d'index nuit.
        <li
          key={`${marque.nomAffichage}-${String(rang)}`}
          className="flex flex-col items-start gap-3"
        >
          {marque.siteUrl === null ? (
            <span className="inline-flex items-center">
              <LogoDeMarque
                adresse={marque.logoUrl}
                nomAffichage={marque.nomAffichage}
              />
            </span>
          ) : (
            <a
              href={marque.siteUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-fit items-center rounded-2"
            >
              <LogoDeMarque
                adresse={marque.logoUrl}
                nomAffichage={marque.nomAffichage}
              />
            </a>
          )}

          {/* **La phrase est AU NOM DE LA STRUCTURE, jamais d'une personne** (F-74), et
              c'est le serveur qui tient cette borne — la colonne est saisie par le compte
              qui voit tout. Ce composant l'affiche telle quelle et n'attribue rien : il
              n'existe **aucun champ d'auteur** à afficher, et c'est exactement ce que
              « AUCUNE CITATION ATTRIBUÉE À UNE PERSONNE NOMMÉE » veut dire au rendu. */}
          {marque.phrase !== null && (
            <blockquote className="text-16 leading-large text-texte">
              {marque.phrase}
            </blockquote>
          )}

          {/* **Un couple chiffre et libellé est une VALEUR SAISIE, jamais un calcul**
              (F-70) : « AUCUN CHIFFRE N'EST CALCULÉ », et « qu'une valeur saisie soit vraie
              n'est pas une décision de produit ». Rien n'est donc formaté, arrondi, ni mis
              en unité ici — le texte part tel qu'il a été écrit, sans quoi le produit
              répondrait d'un chiffre dont il a refusé de répondre. */}
          {marque.chiffres.length > 0 && (
            <dl className="flex flex-wrap gap-6">
              {marque.chiffres.map((chiffre) => (
                // **`flex-col-reverse` et non un libellé recopié en `sr-only`.** L'ordre
                // du document reste `dt` puis `dd`, que la liste de définitions exige ;
                // l'affichage met le chiffre au-dessus de son libellé. Recopier le libellé
                // pour le voir aurait donné **deux textes pour une valeur**, dont un
                // invisible qui se périme sans que rien ne le montre.
                <div key={chiffre.libelle} className="flex flex-col-reverse">
                  <dt className="text-13 text-texte-faible">
                    {chiffre.libelle}
                  </dt>
                  <dd className="text-30 font-weight-800 leading-serre tracking-titre text-texte-fort">
                    {chiffre.valeur}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </li>
      ))}
    </ul>
  );
}
